import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import '@fontsource/material-icons/index.css'
import './SMETechFocusMain.css'

/**
 * SMETechFocus — landing page modelled on
 * `references/webpage_design/SMETechFocus.html` (Material Design 3, dark
 * baseline, indigo-blue primary, teal tertiary).
 *
 * Interactive bits:
 *  - Top app bar gains an elevation/background when the page is scrolled.
 *  - Nav links highlight whichever section is currently in view
 *    (IntersectionObserver), or "Home" when near the top.
 *  - Hero panel rotates a 4-slide telemetry slideshow every 4.5 s with
 *    dot navigation; rotation pauses on mouse hover and resumes on leave.
 *
 * The Map / GeoCartography sub-product links currently point at `/`
 * (the existing GeoCartographyMainPage route). When this component is
 * promoted to `/`, swap those targets to the new GeoCartography route.
 */

type NavKey = 'home' | 'focus' | 'work' | 'about'

type Slide = {
  id: string
  tagIcon: string
  tagText: string
  label: string
  illustration: React.ReactNode
}

// GeoCartography landing route. SMETechFocusMain is the root, so
// GeoCartography sits at /geocartography.
const GEO_CARTOGRAPHY_PATH = '/geocartography'
const NSW_TRANSIT_PATH = '/nsw-transit'

// ------------------------------------------------------------------ slides

const slides: Slide[] = [
  {
    id: 'cold-chain',
    tagIcon: 'ac_unit',
    tagText: '−18 °C · live',
    label: 'Cold-chain container',
    illustration: (
      <svg viewBox="0 0 500 400" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
        <defs>
          <pattern id="smetf-hgrid-0" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M40 0H0V40" fill="none" stroke="var(--md-sys-color-outline-variant)" strokeWidth="1" opacity="0.4" />
          </pattern>
        </defs>
        <rect width="500" height="400" fill="url(#smetf-hgrid-0)" />
        {/* supply route + waypoints */}
        <polyline points="30,330 120,330 230,348 350,330 470,344" fill="none" stroke="var(--md-sys-color-outline)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="30" cy="330" r="7" fill="var(--md-sys-color-surface-container)" stroke="var(--md-sys-color-secondary)" strokeWidth="4" />
        <circle cx="470" cy="344" r="7" fill="var(--md-sys-color-surface-container)" stroke="var(--md-sys-color-primary)" strokeWidth="4" />
        {/* container body */}
        <rect x="120" y="158" width="260" height="120" rx="14" fill="var(--md-sys-color-surface-container-high)" stroke="var(--md-sys-color-primary)" strokeWidth="2.5" />
        {/* corrugation */}
        <g stroke="var(--md-sys-color-primary)" strokeWidth="2" opacity="0.22">
          <line x1="210" y1="166" x2="210" y2="270" />
          <line x1="232" y1="166" x2="232" y2="270" />
          <line x1="254" y1="166" x2="254" y2="270" />
          <line x1="276" y1="166" x2="276" y2="270" />
          <line x1="298" y1="166" x2="298" y2="270" />
          <line x1="320" y1="166" x2="320" y2="270" />
        </g>
        <rect x="344" y="158" width="36" height="120" rx="6" fill="none" stroke="var(--md-sys-color-primary)" strokeWidth="2" opacity="0.6" />
        {/* refrigeration unit */}
        <rect x="120" y="158" width="70" height="120" rx="12" fill="color-mix(in srgb, var(--md-sys-color-tertiary) 22%, transparent)" stroke="var(--md-sys-color-tertiary)" strokeWidth="2" />
        <g stroke="var(--md-sys-color-tertiary)" strokeWidth="2" opacity="0.7">
          <line x1="134" y1="182" x2="176" y2="182" />
          <line x1="134" y1="198" x2="176" y2="198" />
          <line x1="134" y1="214" x2="176" y2="214" />
        </g>
        {/* temp readout */}
        <rect x="210" y="196" width="92" height="44" rx="9" fill="var(--md-sys-color-surface)" stroke="var(--md-sys-color-outline-variant)" strokeWidth="1.5" />
        <g stroke="var(--md-sys-color-tertiary)" strokeWidth="2" strokeLinecap="round">
          <line x1="230" y1="208" x2="230" y2="228" />
          <line x1="221" y1="213" x2="239" y2="223" />
          <line x1="239" y1="213" x2="221" y2="223" />
        </g>
        <text x="252" y="224" fontFamily="var(--md-sys-typescale-plain)" fontSize="17" fontWeight="700" fill="var(--md-sys-color-tertiary)">−18°</text>
        {/* telemetry nodes */}
        <g>
          <circle className="sig-arc"   cx="170" cy="158" r="15" fill="none" stroke="var(--md-sys-color-tertiary)" strokeWidth="2" />
          <circle className="sig-arc b" cx="250" cy="158" r="15" fill="none" stroke="var(--md-sys-color-tertiary)" strokeWidth="2" />
          <circle className="sig-arc c" cx="330" cy="158" r="15" fill="none" stroke="var(--md-sys-color-tertiary)" strokeWidth="2" />
          <circle cx="170" cy="158" r="5" fill="var(--md-sys-color-tertiary)" />
          <circle cx="250" cy="158" r="5" fill="var(--md-sys-color-tertiary)" />
          <circle cx="330" cy="158" r="5" fill="var(--md-sys-color-tertiary)" />
        </g>
        {/* uplink */}
        <path d="M330 158 C 400 110, 430 96, 446 84" fill="none" stroke="var(--md-sys-color-tertiary)" strokeWidth="2" strokeDasharray="4 5" opacity="0.8" />
        <g transform="translate(446,72)">
          <rect x="-18" y="-15" width="36" height="30" rx="8" fill="var(--md-sys-color-tertiary-container)" stroke="var(--md-sys-color-tertiary)" strokeWidth="2" />
          <circle cx="0" cy="0" r="4" fill="var(--md-sys-color-tertiary)" />
        </g>
      </svg>
    ),
  },
  {
    id: 'transit-fleet',
    tagIcon: 'sensors',
    tagText: 'Live telemetry',
    label: 'Transit fleet',
    illustration: (
      <svg viewBox="0 0 500 400" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
        <defs>
          <pattern id="smetf-hgrid-1" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M40 0H0V40" fill="none" stroke="var(--md-sys-color-outline-variant)" strokeWidth="1" opacity="0.4" />
          </pattern>
        </defs>
        <rect width="500" height="400" fill="url(#smetf-hgrid-1)" />
        {/* overhead catenary */}
        <line x1="40" y1="80" x2="460" y2="80" stroke="var(--md-sys-color-outline)" strokeWidth="2" />
        <line x1="250" y1="80" x2="250" y2="128" stroke="var(--md-sys-color-outline)" strokeWidth="2" />
        {/* rail */}
        <line x1="20" y1="312" x2="480" y2="312" stroke="var(--md-sys-color-outline)" strokeWidth="3" />
        <g stroke="var(--md-sys-color-outline-variant)" strokeWidth="3">
          <line x1="60" y1="312" x2="48" y2="330" />
          <line x1="130" y1="312" x2="118" y2="330" />
          <line x1="200" y1="312" x2="188" y2="330" />
          <line x1="270" y1="312" x2="258" y2="330" />
          <line x1="340" y1="312" x2="328" y2="330" />
          <line x1="410" y1="312" x2="398" y2="330" />
        </g>
        {/* pantograph */}
        <polyline points="230,128 250,106 270,128" fill="none" stroke="var(--md-sys-color-on-surface)" strokeWidth="2.5" strokeLinejoin="round" />
        {/* train body */}
        <rect x="90" y="130" width="320" height="125" rx="22" fill="var(--md-sys-color-surface-container-high)" stroke="var(--md-sys-color-primary)" strokeWidth="2.5" />
        <path d="M90 192 q0 -62 62 -62 l0 125 q-62 0 -62 -63 z" fill="color-mix(in srgb, var(--md-sys-color-primary) 22%, transparent)" />
        <rect x="120" y="152" width="260" height="42" rx="10" fill="var(--md-sys-color-primary-container)" opacity="0.9" />
        <line x1="190" y1="152" x2="190" y2="194" stroke="var(--md-sys-color-surface-container-high)" strokeWidth="3" />
        <line x1="250" y1="152" x2="250" y2="194" stroke="var(--md-sys-color-surface-container-high)" strokeWidth="3" />
        <line x1="310" y1="152" x2="310" y2="194" stroke="var(--md-sys-color-surface-container-high)" strokeWidth="3" />
        <rect x="338" y="208" width="42" height="47" rx="4" fill="none" stroke="var(--md-sys-color-primary)" strokeWidth="2" opacity="0.7" />
        {/* bogies */}
        <g fill="var(--md-sys-color-surface-bright)" stroke="var(--md-sys-color-on-surface-variant)" strokeWidth="2">
          <circle cx="150" cy="290" r="18" />
          <circle cx="205" cy="290" r="18" />
          <circle cx="300" cy="290" r="18" />
          <circle cx="355" cy="290" r="18" />
        </g>
        <g fill="var(--md-sys-color-on-surface-variant)">
          <circle cx="150" cy="290" r="5" />
          <circle cx="205" cy="290" r="5" />
          <circle cx="300" cy="290" r="5" />
          <circle cx="355" cy="290" r="5" />
        </g>
        {/* telemetry */}
        <g>
          <circle className="sig-arc"   cx="170" cy="130" r="15" fill="none" stroke="var(--md-sys-color-tertiary)" strokeWidth="2" />
          <circle className="sig-arc b" cx="250" cy="130" r="15" fill="none" stroke="var(--md-sys-color-tertiary)" strokeWidth="2" />
          <circle className="sig-arc c" cx="330" cy="130" r="15" fill="none" stroke="var(--md-sys-color-tertiary)" strokeWidth="2" />
          <circle cx="170" cy="130" r="5" fill="var(--md-sys-color-tertiary)" />
          <circle cx="250" cy="130" r="5" fill="var(--md-sys-color-tertiary)" />
          <circle cx="330" cy="130" r="5" fill="var(--md-sys-color-tertiary)" />
        </g>
        <path d="M330 130 C 400 90, 430 84, 446 72" fill="none" stroke="var(--md-sys-color-tertiary)" strokeWidth="2" strokeDasharray="4 5" opacity="0.8" />
        <g transform="translate(446,64)">
          <rect x="-18" y="-15" width="36" height="30" rx="8" fill="var(--md-sys-color-tertiary-container)" stroke="var(--md-sys-color-tertiary)" strokeWidth="2" />
          <circle cx="0" cy="0" r="4" fill="var(--md-sys-color-tertiary)" />
        </g>
      </svg>
    ),
  },
  {
    id: 'freight-vessel',
    tagIcon: 'sensors',
    tagText: 'Vessel telemetry',
    label: 'Freight vessel',
    illustration: (
      <svg viewBox="0 0 500 400" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
        <defs>
          <pattern id="smetf-hgrid-2" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M40 0H0V40" fill="none" stroke="var(--md-sys-color-outline-variant)" strokeWidth="1" opacity="0.4" />
          </pattern>
        </defs>
        <rect width="500" height="400" fill="url(#smetf-hgrid-2)" />
        {/* water */}
        <path d="M0 300 Q 125 285 250 300 T 500 300 L500 400 L0 400 Z" fill="color-mix(in srgb, var(--md-sys-color-tertiary) 12%, transparent)" />
        <g stroke="var(--md-sys-color-tertiary)" strokeWidth="2" opacity="0.4">
          <line x1="60" y1="332" x2="108" y2="332" />
          <line x1="150" y1="348" x2="198" y2="348" />
          <line x1="320" y1="336" x2="368" y2="336" />
          <line x1="400" y1="350" x2="448" y2="350" />
        </g>
        {/* hull */}
        <path d="M110 250 H400 L368 300 Q250 318 150 300 Z" fill="var(--md-sys-color-surface-container-high)" stroke="var(--md-sys-color-primary)" strokeWidth="2.5" />
        <line x1="122" y1="268" x2="384" y2="268" stroke="var(--md-sys-color-primary)" strokeWidth="2" opacity="0.4" />
        {/* containers */}
        <g>
          <rect x="150" y="206" width="48" height="44" rx="3" fill="var(--md-sys-color-primary-container)" stroke="var(--md-sys-color-primary)" strokeWidth="1.5" />
          <rect x="202" y="206" width="48" height="44" rx="3" fill="color-mix(in srgb, var(--md-sys-color-tertiary) 30%, transparent)" stroke="var(--md-sys-color-tertiary)" strokeWidth="1.5" />
          <rect x="254" y="206" width="48" height="44" rx="3" fill="var(--md-sys-color-secondary-container)" stroke="var(--md-sys-color-secondary)" strokeWidth="1.5" />
          <rect x="176" y="176" width="48" height="30" rx="3" fill="color-mix(in srgb, var(--md-sys-color-tertiary) 30%, transparent)" stroke="var(--md-sys-color-tertiary)" strokeWidth="1.5" />
          <rect x="228" y="176" width="48" height="30" rx="3" fill="var(--md-sys-color-primary-container)" stroke="var(--md-sys-color-primary)" strokeWidth="1.5" />
        </g>
        {/* bridge */}
        <rect x="312" y="180" width="56" height="70" rx="6" fill="var(--md-sys-color-surface-container-highest)" stroke="var(--md-sys-color-primary)" strokeWidth="2" />
        <g fill="var(--md-sys-color-primary-container)">
          <rect x="322" y="192" width="36" height="9" rx="2" />
          <rect x="322" y="206" width="36" height="9" rx="2" />
        </g>
        <line x1="340" y1="180" x2="340" y2="140" stroke="var(--md-sys-color-on-surface)" strokeWidth="2" />
        {/* telemetry */}
        <g>
          <circle className="sig-arc"   cx="340" cy="138" r="15" fill="none" stroke="var(--md-sys-color-tertiary)" strokeWidth="2" />
          <circle className="sig-arc b" cx="200" cy="176" r="13" fill="none" stroke="var(--md-sys-color-tertiary)" strokeWidth="2" />
          <circle cx="340" cy="138" r="5" fill="var(--md-sys-color-tertiary)" />
          <circle cx="200" cy="176" r="4" fill="var(--md-sys-color-tertiary)" />
        </g>
        <path d="M340 138 C 390 110, 425 92, 446 76" fill="none" stroke="var(--md-sys-color-tertiary)" strokeWidth="2" strokeDasharray="4 5" opacity="0.8" />
        <g transform="translate(446,68)">
          <rect x="-18" y="-15" width="36" height="30" rx="8" fill="var(--md-sys-color-tertiary-container)" stroke="var(--md-sys-color-tertiary)" strokeWidth="2" />
          <circle cx="0" cy="0" r="4" fill="var(--md-sys-color-tertiary)" />
        </g>
      </svg>
    ),
  },
  {
    id: 'haul-truck',
    tagIcon: 'sensors',
    tagText: 'Haul telemetry',
    label: 'Haul truck',
    illustration: (
      <svg viewBox="0 0 500 400" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
        <defs>
          <pattern id="smetf-hgrid-3" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M40 0H0V40" fill="none" stroke="var(--md-sys-color-outline-variant)" strokeWidth="1" opacity="0.4" />
          </pattern>
        </defs>
        <rect width="500" height="400" fill="url(#smetf-hgrid-3)" />
        <line x1="20" y1="296" x2="480" y2="296" stroke="var(--md-sys-color-outline)" strokeWidth="3" />
        {/* tray */}
        <path d="M150 140 L378 158 L398 206 L150 206 Z" fill="var(--md-sys-color-surface-container-high)" stroke="var(--md-sys-color-primary)" strokeWidth="2.5" />
        <path d="M160 150 Q 250 134 366 158 L360 150 Q 250 132 170 140 Z" fill="color-mix(in srgb, var(--md-sys-color-tertiary) 22%, transparent)" />
        <g stroke="var(--md-sys-color-primary)" strokeWidth="2" opacity="0.22">
          <line x1="212" y1="160" x2="214" y2="206" />
          <line x1="272" y1="166" x2="274" y2="206" />
          <line x1="332" y1="172" x2="334" y2="206" />
        </g>
        <rect x="150" y="206" width="248" height="20" rx="4" fill="var(--md-sys-color-surface-container-highest)" stroke="var(--md-sys-color-on-surface-variant)" strokeWidth="1.5" />
        {/* cab */}
        <rect x="116" y="160" width="46" height="66" rx="6" fill="var(--md-sys-color-primary-container)" stroke="var(--md-sys-color-primary)" strokeWidth="2" />
        <rect x="125" y="170" width="28" height="22" rx="3" fill="var(--md-sys-color-surface)" opacity="0.5" />
        <g stroke="var(--md-sys-color-on-surface-variant)" strokeWidth="3" strokeLinecap="round">
          <line x1="170" y1="140" x2="170" y2="120" />
          <line x1="182" y1="140" x2="182" y2="120" />
        </g>
        {/* wheels */}
        <g fill="var(--md-sys-color-surface-bright)" stroke="var(--md-sys-color-on-surface-variant)" strokeWidth="3">
          <circle cx="220" cy="262" r="34" />
          <circle cx="338" cy="262" r="34" />
        </g>
        <g fill="var(--md-sys-color-on-surface-variant)">
          <circle cx="220" cy="262" r="12" />
          <circle cx="338" cy="262" r="12" />
        </g>
        {/* telemetry */}
        <g>
          <circle className="sig-arc"   cx="139" cy="160" r="14" fill="none" stroke="var(--md-sys-color-tertiary)" strokeWidth="2" />
          <circle className="sig-arc b" cx="320" cy="150" r="14" fill="none" stroke="var(--md-sys-color-tertiary)" strokeWidth="2" />
          <circle cx="139" cy="160" r="5" fill="var(--md-sys-color-tertiary)" />
          <circle cx="320" cy="150" r="5" fill="var(--md-sys-color-tertiary)" />
        </g>
        <path d="M320 150 C 385 108, 425 90, 446 74" fill="none" stroke="var(--md-sys-color-tertiary)" strokeWidth="2" strokeDasharray="4 5" opacity="0.8" />
        <g transform="translate(446,66)">
          <rect x="-18" y="-15" width="36" height="30" rx="8" fill="var(--md-sys-color-tertiary-container)" stroke="var(--md-sys-color-tertiary)" strokeWidth="2" />
          <circle cx="0" cy="0" r="4" fill="var(--md-sys-color-tertiary)" />
        </g>
      </svg>
    ),
  },
]

// ---------------------------------------------------------------- component

const SMETechFocusMain = () => {
  const [activeSlide, setActiveSlide] = useState(0)
  const [activeNav, setActiveNav] = useState<NavKey>('home')
  const [appBarScrolled, setAppBarScrolled] = useState(false)

  const panelRef = useRef<HTMLDivElement | null>(null)
  const intervalRef = useRef<number | null>(null)
  const isHoveringRef = useRef(false)

  // ----- Sticky app-bar shadow on scroll -----
  useEffect(() => {
    const onScroll = () => {
      setAppBarScrolled(window.scrollY > 8)
      if (window.scrollY < 200) setActiveNav('home')
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // ----- Scroll-spy nav highlighting -----
  useEffect(() => {
    const ids: NavKey[] = ['focus', 'work', 'about']
    const sections = ids
      .map(id => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null)
    if (sections.length === 0) return

    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveNav(entry.target.id as NavKey)
          }
        }
      },
      { rootMargin: '-50% 0px -45% 0px', threshold: 0 }
    )
    sections.forEach(s => observer.observe(s))
    return () => observer.disconnect()
  }, [])

  // ----- Hero slideshow rotation -----
  useEffect(() => {
    const start = () => {
      stop()
      intervalRef.current = window.setInterval(() => {
        if (!isHoveringRef.current) {
          setActiveSlide(i => (i + 1) % slides.length)
        }
      }, 4500)
    }
    const stop = () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
    start()
    return stop
  }, [])

  const handlePanelEnter = () => {
    isHoveringRef.current = true
  }
  const handlePanelLeave = () => {
    isHoveringRef.current = false
  }
  const goToSlide = (n: number) => {
    setActiveSlide(((n % slides.length) + slides.length) % slides.length)
  }

  const currentSlide = slides[activeSlide]

  // Anchor click handler: smooth-scroll instead of jump.
  const onAnchorClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault()
    const target = id === 'top' ? document.body : document.getElementById(id)
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <div className="smetechfocus-main">

      {/* ============ TOP APP BAR ============ */}
      <header className={`md-top-app-bar${appBarScrolled ? ' md-top-app-bar--scrolled' : ''}`}>
        <div className="appbar-inner">
          <a className="brand" href="#top" onClick={e => onAnchorClick(e, 'top')} aria-label="SMETechFocus home">
            <span className="brand__mark"><span className="material-icons" aria-hidden="true">hub</span></span>
            <span className="brand__text">
              <b>SMETechFocus</b>
              <span>IoT · logistics &amp; transit</span>
            </span>
          </a>
          <nav className="nav-links" aria-label="Primary">
            <a
              href="#top"
              onClick={e => onAnchorClick(e, 'top')}
              className={`md-nav-link${activeNav === 'home' ? ' md-nav-link--active' : ''}`}
            >Home</a>
            <a
              href="#focus"
              onClick={e => onAnchorClick(e, 'focus')}
              className={`md-nav-link${activeNav === 'focus' ? ' md-nav-link--active' : ''}`}
            >Focus</a>
            <a
              href="#work"
              onClick={e => onAnchorClick(e, 'work')}
              className={`md-nav-link${activeNav === 'work' ? ' md-nav-link--active' : ''}`}
            >Work</a>
            <a
              href="#about"
              onClick={e => onAnchorClick(e, 'about')}
              className={`md-nav-link${activeNav === 'about' ? ' md-nav-link--active' : ''}`}
            >About</a>
          </nav>
          <div className="appbar-actions">
            <a className="md-button md-button--filled has-icon" href="#contact" onClick={e => onAnchorClick(e, 'contact')}>
              <span className="material-icons" aria-hidden="true">mail</span>
              Get in touch
            </a>
          </div>
        </div>
      </header>

      <main id="top">

        {/* ============ HERO ============ */}
        <section className="hero">
          <div className="container hero-grid">
            <div>
              <span className="hero-eyebrow md-label-large">
                <span className="material-icons" aria-hidden="true">widgets</span>
                Logistics · Transit · Connected data
              </span>
              <h1 className="md-display-large">
                Engineering the data layer for{' '}
                <span className="accent">connected logistics &amp; transit</span>.
              </h1>
              <p className="lead md-body-large">
                SMETechFocus designs IoT, sensor and data-communication systems
                that make logistics and transit systems observable, reliable and
                smart, from the cold chain to live fleet tracking.
              </p>
              <div className="hero-cta">
                <a className="md-button md-button--filled has-icon" href="#work" onClick={e => onAnchorClick(e, 'work')}>
                  <span className="material-icons" aria-hidden="true">dashboard</span>
                  See featured work
                </a>
                <a className="md-button md-button--outlined has-icon" href="#contact" onClick={e => onAnchorClick(e, 'contact')}>
                  <span className="material-icons" aria-hidden="true">forum</span>
                  Start a conversation
                </a>
              </div>
            </div>

            {/* Hero panel — rotating telemetry slideshow */}
            <div
              ref={panelRef}
              className="hero-panel"
              role="group"
              aria-label="Connected-asset telemetry, illustrated"
              onMouseEnter={handlePanelEnter}
              onMouseLeave={handlePanelLeave}
            >
              <div className="hero-tag">
                <span className="material-icons" aria-hidden="true">{currentSlide.tagIcon}</span>
                <span>{currentSlide.tagText}</span>
              </div>
              <div className="hero-stage">
                {slides.map((slide, idx) => (
                  <div
                    key={slide.id}
                    className={`hero-slide${idx === activeSlide ? ' active' : ''}`}
                    aria-label={slide.label}
                  >
                    {slide.illustration}
                  </div>
                ))}
              </div>
              <div className="hero-dots" role="tablist" aria-label="Slideshow navigation">
                {slides.map((slide, idx) => (
                  <button
                    key={slide.id}
                    type="button"
                    className={idx === activeSlide ? 'active' : ''}
                    onClick={() => goToSlide(idx)}
                    aria-label={slide.label}
                    aria-selected={idx === activeSlide}
                    role="tab"
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ============ FOCUS ============ */}
        <section className="block block--alt" id="focus">
          <div className="container">
            <div className="sec-head">
              <h2 className="md-headline-large">Connected systems, end to end</h2>
              <p className="md-body-large">
                A focused practice in IoT, data communications and embedded
                systems for logistics, transit systems and the cold chain.
              </p>
            </div>
            <div className="focus-grid">
              <div className="md-card md-card--elevated focus-card">
                <div className="fc-icon fc-icon--p">
                  <span className="material-icons" aria-hidden="true">local_shipping</span>
                </div>
                <h3 className="md-title-large">Logistics IoT solutions</h3>
                <p className="md-body-medium">
                  Asset and fleet tracking, connected sensors and gateways
                  across supply chains and transit fleets, designed for the
                  field and not just the lab.
                </p>
              </div>
              <div className="md-card md-card--elevated focus-card">
                <div className="fc-icon fc-icon--t">
                  <span className="material-icons" aria-hidden="true">ac_unit</span>
                </div>
                <h3 className="md-title-large">Cold chain design</h3>
                <p className="md-body-medium">
                  Temperature-monitored logistics and sensor-driven compliance
                  for perishables and pharma, with continuous visibility from
                  origin to delivery.
                </p>
              </div>
              <div className="md-card md-card--elevated focus-card">
                <div className="fc-icon fc-icon--s">
                  <span className="material-icons" aria-hidden="true">device_hub</span>
                </div>
                <h3 className="md-title-large">Data communications &amp; telemetry</h3>
                <p className="md-body-medium">
                  Edge-to-cloud connectivity over LoRaWAN, cellular and MQTT,
                  plus the data backbone that turns raw signals into operational
                  insight.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ============ FEATURED WORK ============ */}
        <section className="block" id="work">
          <div className="container">
            <div className="sec-head">
              <p className="sec-eyebrow md-label-large">Featured work</p>
              <h2 className="md-headline-large">GeoCartography: geospatial mapping</h2>
              <p className="md-body-large">
                A worked example of the practice: live asset positions,
                geospatial data and cartography brought together in one
                interactive platform.
              </p>
            </div>

            <div className="work-card">
              <Link to={GEO_CARTOGRAPHY_PATH} className="work-card__media" aria-label="Open GeoCartography">
                <svg viewBox="0 0 520 360" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
                  <defs>
                    <pattern id="smetf-wgrid" width="40" height="40" patternUnits="userSpaceOnUse">
                      <path d="M40 0H0V40" fill="none" stroke="var(--md-sys-color-outline-variant)" strokeWidth="1" opacity="0.45" />
                    </pattern>
                  </defs>
                  <rect width="520" height="360" fill="url(#smetf-wgrid)" />
                  <path d="M0 250 Q 150 220 260 250 T 520 230 L520 300 Q 360 320 240 300 T 0 320 Z" fill="color-mix(in srgb, var(--md-sys-color-tertiary) 12%, transparent)" />
                  <polyline points="70,300 110,210 200,170 320,170 380,110 460,70" fill="none" stroke="var(--md-sys-color-primary)" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
                  <polyline points="120,330 150,230 250,230 330,150 330,60" fill="none" stroke="var(--md-sys-color-tertiary)" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" opacity="0.9" />
                  <g>
                    <circle cx="110" cy="210" r="7" fill="var(--md-sys-color-surface)" stroke="var(--md-sys-color-primary)" strokeWidth="4" />
                    <circle cx="200" cy="170" r="7" fill="var(--md-sys-color-surface)" stroke="var(--md-sys-color-primary)" strokeWidth="4" />
                    <circle cx="320" cy="170" r="7" fill="var(--md-sys-color-surface)" stroke="var(--md-sys-color-primary)" strokeWidth="4" />
                    <circle cx="250" cy="230" r="7" fill="var(--md-sys-color-surface)" stroke="var(--md-sys-color-tertiary)" strokeWidth="4" />
                  </g>
                </svg>
                <span className="pin" style={{ left: '62%', top: '46%' }}>
                  <span className="material-icons" aria-hidden="true">location_on</span>
                </span>
              </Link>
              <div className="work-card__body">
                <span className="kicker md-label-large">
                  <span className="material-icons" aria-hidden="true">map</span>
                  Interactive platform
                </span>
                <h3 className="md-headline-small">Live mapping for fleets and transit networks</h3>
                <p className="md-body-large">
                  An interactive map that renders live vehicle positions across
                  a transit network and logistics fleets, with route layers and
                  quick location search. Geospatial data and telemetry working
                  together, built on MapLibre.
                </p>
                <div className="work-chips">
                  <span className="md-chip"><span className="material-icons" aria-hidden="true">explore</span>Geospatial data</span>
                  <span className="md-chip"><span className="material-icons" aria-hidden="true">sensors</span>Live tracking</span>
                  <span className="md-chip"><span className="material-icons" aria-hidden="true">map</span>Cartography</span>
                </div>
                <div className="work-actions">
                  <Link to={GEO_CARTOGRAPHY_PATH} className="md-button md-button--outlined has-icon">
                    <span className="material-icons" aria-hidden="true">explore</span>
                    GeoCartography
                  </Link>
                  <Link to={NSW_TRANSIT_PATH} className="md-button md-button--filled has-icon">
                    <span className="material-icons" aria-hidden="true">open_in_new</span>
                    View project
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============ ABOUT ============ */}
        <section className="block block--alt" id="about">
          <div className="container about-grid">
            <div className="about-copy">
              <p className="sec-eyebrow md-label-large">About</p>
              <h2 className="md-headline-large" style={{ margin: '0 0 24px' }}>
                Engineering discipline, applied to logistics &amp; transit
              </h2>
              <p className="md-body-large">
                SMETechFocus is an independent engineering practice focused on
                IoT, data communications and embedded systems for logistics and
                transit systems.
              </p>
              <p className="md-body-large">
                The work sits where connected hardware meets data: designing
                sensor and telemetry systems, and the data backbone that keeps
                goods and fleets observable, from the cold chain to live
                tracking across the network.
              </p>
              <div className="about-chips">
                <span className="md-chip"><span className="material-icons" aria-hidden="true">memory</span>Embedded systems</span>
                <span className="md-chip"><span className="material-icons" aria-hidden="true">local_shipping</span>Logistics IoT</span>
                <span className="md-chip"><span className="material-icons" aria-hidden="true">commute</span>Transit &amp; fleets</span>
                <span className="md-chip"><span className="material-icons" aria-hidden="true">ac_unit</span>Cold chain</span>
                <span className="md-chip"><span className="material-icons" aria-hidden="true">sensors</span>Data &amp; telemetry</span>
              </div>
            </div>
            <div className="about-aside">
              <div className="fact">
                <span className="material-icons" aria-hidden="true">engineering</span>
                <div><b>Systems engineering</b><span>Hardware + data, end to end</span></div>
              </div>
              <div className="fact">
                <span className="material-icons" aria-hidden="true">hub</span>
                <div><b>Connected assets</b><span>Telemetry, comms &amp; monitoring</span></div>
              </div>
              <div className="fact">
                <span className="material-icons" aria-hidden="true">verified</span>
                <div><b>Built for the field</b><span>Reliable, compliant deployments</span></div>
              </div>
            </div>
          </div>
        </section>

        {/* ============ CONTACT ============ */}
        <section className="block" id="contact">
          <div className="container">
            <div className="cta-band">
              <h2 className="md-headline-large">Got a logistics, transit or IoT challenge?</h2>
              <p className="md-body-large">
                Whether it's a cold-chain deployment, a connected-sensor
                rollout or a data-communications problem, let's talk about it.
              </p>
              <a className="md-button md-button--filled has-icon" href="mailto:andrew@smetechfocus.com.au">
                <span className="material-icons" aria-hidden="true">mail</span>
                andrew@smetechfocus.com.au
              </a>
            </div>
          </div>
        </section>
      </main>

      {/* ============ FOOTER ============ */}
      <footer>
        <div className="container">
          <div className="footer-grid">
            <div className="footer-brand">
              <a className="brand" href="#top" onClick={e => onAnchorClick(e, 'top')} aria-label="SMETechFocus home">
                <span className="brand__mark"><span className="material-icons" aria-hidden="true">hub</span></span>
                <span className="brand__text">
                  <b>SMETechFocus</b>
                  <span>IoT · logistics &amp; transit</span>
                </span>
              </a>
              <p className="md-body-medium">
                IoT, data communications, cloud infrastructure and embedded systems for machines, logistics and
                transit systems, built in Australia.
              </p>
            </div>
            <div className="footer-col">
              <h4 className="md-title-medium">Explore</h4>
              <ul className="md-body-medium">
                <li><a href="#focus" onClick={e => onAnchorClick(e, 'focus')}>Focus areas</a></li>
                <li><a href="#work" onClick={e => onAnchorClick(e, 'work')}>Featured work</a></li>
                <li><Link to={GEO_CARTOGRAPHY_PATH}>GeoCartography</Link></li>
              </ul>
            </div>
            <div className="footer-col">
              <h4 className="md-title-medium">Contact</h4>
              <ul className="md-body-medium">
                <li className="footer-contact">
                  <span className="material-icons" aria-hidden="true">mail</span>
                  andrew@smetechfocus.com.au
                </li>
                <li className="footer-contact">
                  <span className="material-icons" aria-hidden="true">location_on</span>
                  Australia
                </li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <span className="md-body-small">
              © 2026 SMETechFocus Pty Ltd (ABN: 58 169 611 607). Connected systems for logistics &amp; transit.
            </span>
            <div className="footer-social">
              <a className="md-icon-button" href="mailto:andrew@smetechfocus.com.au" aria-label="Email">
                <span className="material-icons" aria-hidden="true">mail</span>
              </a>
              <Link to={NSW_TRANSIT_PATH} className="md-icon-button" aria-label="GeoCartography">
                <span className="material-icons" aria-hidden="true">map</span>
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default SMETechFocusMain
